import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrintIcon from '@mui/icons-material/Print';
import BarcodeIcon from './BarcodeIcon';
import { useSnackbar } from 'notistack';
import CodigoBarrasPreview from './CodigoBarrasPreview';
import { generarCodigoBarraDesdeArticulo } from '../../utils/codigoBarras';
import productoService from '../../services/productoService';

export interface ProductoCodigosBarraSectionProps {
  codBarra: string;
  codigoArticulo: string;
  descripcion?: string | null;
  disabled?: boolean;
  productoGuardado: boolean;
  onCodBarraChange: (value: string) => void;
  onImprimir: () => void;
}

const ProductoCodigosBarraSection: React.FC<ProductoCodigosBarraSectionProps> = ({
  codBarra,
  codigoArticulo,
  descripcion,
  disabled,
  productoGuardado,
  onCodBarraChange,
  onImprimir,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmGenerarOpen, setConfirmGenerarOpen] = useState(false);
  const [errorDuplicado, setErrorDuplicado] = useState<string | null>(null);
  const [verificando, setVerificando] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const valor = (codBarra || '').trim();
    if (!valor) {
      setErrorDuplicado(null);
      setVerificando(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(() => {
      void (async () => {
        setVerificando(true);
        try {
          const otro = await productoService.codigoBarraEnUsoPorOtro(valor, codigoArticulo);
          if (requestId !== requestIdRef.current) return;
          if (otro) {
            setErrorDuplicado(
              `Ya lo usa el producto ${otro.codigoArticulo}${otro.descripcion ? ` (${otro.descripcion})` : ''}.`,
            );
          } else {
            setErrorDuplicado(null);
          }
        } catch {
          if (requestId !== requestIdRef.current) return;
          setErrorDuplicado(null);
        } finally {
          if (requestId === requestIdRef.current) setVerificando(false);
        }
      })();
    }, 450);
    return () => {
      window.clearTimeout(timer);
    };
  }, [codBarra, codigoArticulo]);

  const aplicarGenerado = async () => {
    try {
      const generado = generarCodigoBarraDesdeArticulo(codigoArticulo);
      const otro = await productoService.codigoBarraEnUsoPorOtro(generado, codigoArticulo);
      if (otro) {
        enqueueSnackbar(
          `Ese código de barras ya está en el producto ${otro.codigoArticulo}. No puede repetirse.`,
          { variant: 'warning' },
        );
        return;
      }
      onCodBarraChange(generado);
      enqueueSnackbar('Código generado. Guardá el producto para conservarlo.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'No se pudo generar el código', { variant: 'warning' });
    }
  };

  const handleGenerar = () => {
    if (!(codigoArticulo || '').trim()) {
      enqueueSnackbar('Ingresá el código de artículo primero (solapa General).', { variant: 'warning' });
      return;
    }
    if ((codBarra || '').trim()) {
      setConfirmGenerarOpen(true);
      return;
    }
    void aplicarGenerado();
  };

  const handleCopiar = async () => {
    const valor = (codBarra || '').trim();
    if (!valor) {
      enqueueSnackbar('No hay código para copiar', { variant: 'info' });
      return;
    }
    try {
      await navigator.clipboard.writeText(valor);
      enqueueSnackbar('Código copiado', { variant: 'success' });
    } catch {
      enqueueSnackbar('No se pudo copiar al portapapeles', { variant: 'error' });
    }
  };

  const helperBase = 'Escribí, escaneá o generá un código. No puede repetirse en otro producto.';

  return (
    <>
      <GridDosColumnas>
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          <TextField
            name="codBarra"
            label="Código de Barras"
            value={codBarra || ''}
            onChange={(e) => onCodBarraChange(e.target.value)}
            fullWidth
            disabled={disabled}
            error={!!errorDuplicado}
            helperText={errorDuplicado || (verificando ? 'Verificando que no esté en otro producto…' : helperBase)}
            inputProps={{ autoComplete: 'off', 'aria-label': 'Código de barras' }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<BarcodeIcon />}
              onClick={handleGenerar}
              disabled={disabled}
              sx={{ textTransform: 'none', borderRadius: 2, minHeight: 44 }}
            >
              Generar código
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopiar}
              disabled={disabled || !(codBarra || '').trim()}
              sx={{ textTransform: 'none', borderRadius: 2, minHeight: 44 }}
            >
              Copiar
            </Button>
            <Tooltip
              title={productoGuardado ? 'Imprimir etiquetas de este producto' : 'Guardá el producto para imprimir'}
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={onImprimir}
                  disabled={disabled || !productoGuardado}
                  sx={{ textTransform: 'none', borderRadius: 2, minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
                >
                  Imprimir etiquetas
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Vista previa
            </Typography>
            <CodigoBarrasPreview valor={codBarra} descripcion={descripcion} minHeight={148} />
          </CardContent>
        </Card>
      </GridDosColumnas>

      <Dialog open={confirmGenerarOpen} onClose={() => setConfirmGenerarOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>¿Reemplazar el código actual?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Ya hay un código cargado. Generar uno nuevo lo va a reemplazar (todavía tenés que guardar el producto).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmGenerarOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmGenerarOpen(false);
              void aplicarGenerado();
            }}
            sx={{ textTransform: 'none' }}
          >
            Reemplazar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const GridDosColumnas: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      alignItems: 'stretch',
      mb: 1,
    }}
  >
    {children}
  </Box>
);

export default ProductoCodigosBarraSection;
